/**
 * Pipeline tab component.
 *
 * Displays CI/CD pipeline status for all 13 repositories.
 *
 * Implementation plan reference: D2.15, D2.15a (no env on getPipeline; no refetch on env change)
 */

import { useEffect, useRef, useState } from 'react';
import { ApiError, getAccessibility, getPipeline, getServices } from '@/api/client';
import { POLL_INTERVAL_PIPELINE_MS, useRefresh } from '@/context/RefreshContext';
import { StagePill } from '@/components/StagePill';
import { Badge } from '@/components/Badge';
import { formatDateTime, formatRelativeAge } from '@/utils/format';
import type {
  MicroservicePipeline,
  OpenPr,
  OtherRepoPipeline,
  PipelineResponse,
  PipelineStage,
  PipelineWarning,
  PrReviewStatus,
  StageStatus,
  EcsService,
  Pa11yAccessibilityReport,
} from '@/types/api';

/**
 * Warning indicator component - shows a warning triangle with tooltip
 */
function WarningIndicator({ warning }: { warning: PipelineWarning }): JSX.Element {
  const [expanded, setExpanded] = useState(false);

  return (
    <span className="warning-indicator">
      <button
        type="button"
        className="warning-trigger"
        onClick={(): void => setExpanded(!expanded)}
        aria-expanded={expanded}
        aria-label={`Warning for ${warning.repo}`}
        title={warning.message}
      >
        ⚠️
      </button>
      {expanded && (
        <span className="warning-tooltip">{warning.message}</span>
      )}
    </span>
  );
}

function orderedMicroserviceStages(stages: MicroservicePipeline['stages']): PipelineStage[] {
  const keys: (keyof MicroservicePipeline['stages'])[] = [
    'buildAndUnitTests',
    'integrationTests',
    'ecsDeployDev',
    'functionalTests',
    'ecsDeployStaging',
    'ecsDeployProd',
  ];
  return keys.map((k) => stages[k]);
}

function otherRepoBadgeVariant(status: StageStatus): 'success' | 'warning' | 'error' | 'info' | 'neutral' {
  switch (status) {
    case 'passed':
      return 'success';
    case 'failed':
      return 'error';
    case 'running':
      return 'info';
    case 'pending-gate':
      return 'warning';
    default:
      return 'neutral';
  }
}

const formatWhen = (iso: string): string =>
  formatDateTime(iso, { dateStyle: 'medium', timeStyle: 'short' });

// formatAge previously local — extracted to @/utils/format as formatRelativeAge
// (2026-08-31). Two call sites in this file below now call the shared helper
// directly rather than through a local alias.

function LastProdPromoteCard({ lastDeployedProdAt }: { lastDeployedProdAt?: string }): JSX.Element {
  const has = lastDeployedProdAt !== undefined && lastDeployedProdAt !== '';

  return (
    <aside className="last-prod-promote-card" aria-label="Platform-wide last production promote">
      <h3 className="last-prod-promote-title">Last prod promote</h3>
      {has ? (
        <p className="last-prod-promote-value">
          <time dateTime={lastDeployedProdAt} title={formatWhen(lastDeployedProdAt)}>
            {formatRelativeAge(lastDeployedProdAt)}
          </time>
          <span className="last-prod-promote-local"> ({formatWhen(lastDeployedProdAt)})</span>
        </p>
      ) : (
        <p className="last-prod-promote-value last-prod-promote-empty">No prod deploy recorded</p>
      )}
      <p className="last-prod-promote-hint">
        Latest successful CloudFormation deploy to prod with an image tag (GitHub Actions).
      </p>
    </aside>
  );
}

function PrReviewStatusIndicator({ status }: { status: PrReviewStatus }): JSX.Element {
  switch (status) {
    case 'approved':
      return <span className="pr-review-status pr-review-status--approved" title="Approved">✓</span>;
    case 'changes-requested':
      return <span className="pr-review-status pr-review-status--changes" title="Changes requested">✗</span>;
    case 'unreviewed':
    default:
      return <span className="pr-review-status pr-review-status--unreviewed" title="Unreviewed">–</span>;
  }
}

function PrBadge({ openPrCount, openPrs }: { openPrCount?: number; openPrs?: OpenPr[] }): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return undefined;

    function handleClickOutside(e: MouseEvent): void {
      if (containerRef.current !== null && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleEscape(e: KeyboardEvent): void {
      if (e.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  // Handle missing fields (API may not return these yet)
  if (openPrCount === undefined) return null;

  const hasPrs = openPrCount > 0 && openPrs !== undefined && openPrs.length > 0;

  // Show green badge for 0 PRs (no popover needed)
  if (!hasPrs) {
    return (
      <span className="pr-badge-container">
        <span className="pr-badge-btn pr-badge-btn--zero" aria-label="No open pull requests">
          0 PRs
        </span>
      </span>
    );
  }

  // Show purple interactive badge for 1+ PRs with popover
  return (
    <span className="pr-badge-container" ref={containerRef}>
      <button
        type="button"
        className="pr-badge-btn"
        onClick={(): void => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={`${openPrCount} open pull request${openPrCount !== 1 ? 's' : ''}`}
      >
        {openPrCount} PR{openPrCount !== 1 ? 's' : ''}
      </button>
      {open && (
        <div className="pr-popover" role="dialog" aria-label="Open pull requests">
          <ul className="pr-popover-list">
            {openPrs.map((pr) => (
              <li key={pr.number} className={`pr-popover-item${pr.draft ? ' pr-popover-item--draft' : ''}`}>
                <div className="pr-popover-title-row">
                  <PrReviewStatusIndicator status={pr.reviewStatus} />
                  <a
                    href={pr.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`pr-popover-link${pr.draft ? ' pr-popover-link--draft' : ''}`}
                  >
                    #{pr.number} {pr.title}
                  </a>
                </div>
                <span className="pr-popover-meta">
                  {pr.draft && <span className="pr-draft-badge">Draft</span>}
                  <span className="pr-popover-age">{formatRelativeAge(pr.createdAt)}</span>
                  <span className="pr-popover-author">{pr.author}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </span>
  );
}

const STAGE_SHORT_LABELS: Record<keyof MicroservicePipeline['stages'], string> = {
  buildAndUnitTests: 'Build',
  integrationTests: 'Int',
  ecsDeployDev: 'Dev',
  functionalTests: 'FT',
  ecsDeployStaging: 'Stg',
  ecsDeployProd: 'Prod',
};

function statusToColor(
  status: StageStatus,
): 'green' | 'red' | 'yellow' | 'blue' | 'gray' | 'not-implemented' {
  switch (status) {
    case 'passed':
      return 'green';
    case 'failed':
      return 'red';
    case 'running':
      return 'blue';
    case 'pending-gate':
      return 'yellow';
    case 'not-implemented':
      return 'not-implemented';
    case 'blocked':
    default:
      return 'gray';
  }
}

function MiniStageIndicator({
  stages,
}: {
  stages: MicroservicePipeline['stages'];
}): JSX.Element {
  const keys: (keyof MicroservicePipeline['stages'])[] = [
    'buildAndUnitTests',
    'integrationTests',
    'ecsDeployDev',
    'functionalTests',
    'ecsDeployStaging',
    'ecsDeployProd',
  ];

  return (
    <div className="mini-stage-row">
      {keys.map((key) => {
        const stage = stages[key];
        const color = statusToColor(stage.status);
        return (
          <div
            key={key}
            className={`mini-stage mini-stage-${color}`}
            title={`${STAGE_SHORT_LABELS[key]}: ${stage.status}`}
          >
            <span className="mini-stage-label">{STAGE_SHORT_LABELS[key]}</span>
          </div>
        );
      })}
    </div>
  );
}

function formatDeployTime(
  stages: MicroservicePipeline['stages'],
  repo: string,
  prodEcsLastDeployedByRepo?: Map<string, string>,
): string | null {
  const prodDeploy = stages.ecsDeployProd;
  if (prodDeploy.status === 'passed' && prodDeploy.completedAt !== undefined && prodDeploy.completedAt !== '') {
    return formatWhen(prodDeploy.completedAt);
  }
  if (prodDeploy.status === 'running' && prodDeploy.startedAt !== undefined && prodDeploy.startedAt !== '') {
    return `deploying since ${formatWhen(prodDeploy.startedAt)}`;
  }
  const ecsIso = prodEcsLastDeployedByRepo?.get(repo);
  if (ecsIso !== undefined && ecsIso !== '') {
    return formatWhen(ecsIso);
  }
  return null;
}

function MicroserviceRow({
  row,
  expanded,
  onToggle,
  warning,
  prodEcsLastDeployedByRepo,
}: {
  row: MicroservicePipeline;
  expanded: boolean;
  onToggle: () => void;
  warning?: PipelineWarning;
  prodEcsLastDeployedByRepo?: Map<string, string>;
}): JSX.Element {
  const stages = orderedMicroserviceStages(row.stages);
  const commitShort = row.commitSha.length > 7 ? row.commitSha.slice(0, 7) : row.commitSha;
  const deployTime = formatDeployTime(row.stages, row.repo, prodEcsLastDeployedByRepo);

  return (
    <div className={`pipeline-microservice ${expanded ? 'pipeline-microservice-expanded' : ''} ${warning !== undefined ? 'pipeline-microservice-warning' : ''}`}>
      <div className="pipeline-microservice-header">
        <button
          type="button"
          className="pipeline-expand-btn"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-label={expanded ? `Collapse ${row.repo}` : `Expand ${row.repo}`}
        >
          <span className="pipeline-expand-icon" aria-hidden>
            {expanded ? '▼' : '▶'}
          </span>
        </button>
        <div className="pipeline-microservice-title">
          <span className="pipeline-repo">
            {row.repo}
            {warning !== undefined && <WarningIndicator warning={warning} />}
            <PrBadge openPrCount={row.openPrCount} openPrs={row.openPrs} />
          </span>
          <span className="pipeline-meta">
            <span className="pipeline-branch">{row.branch}</span>
            <code className="pipeline-commit">{commitShort}</code>
            <span className="pipeline-triggered">{formatWhen(row.triggeredAt)}</span>
          </span>
        </div>
        {!expanded && (
          <div className="pipeline-summary">
            <MiniStageIndicator stages={row.stages} />
            {deployTime !== null && (
              <span className="pipeline-deploy-time">Prod Deployed: {deployTime}</span>
            )}
          </div>
        )}
        <a
          className="pipeline-run-link"
          href={row.runUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Run #{row.runId}
        </a>
      </div>
      {expanded && (
        <div className="pipeline-stages">
          {stages.map((stage, index) => (
            <StagePill
              key={`${stage.name}-${index}`}
              name={stage.name}
              status={stage.status}
              durationSecs={stage.durationSeconds ?? null}
              completedAt={stage.completedAt ?? null}
              url={stage.url ?? null}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function OtherRepoRow({
  row,
  warning,
}: {
  row: OtherRepoPipeline;
  warning?: PipelineWarning;
}): JSX.Element {
  const commitShort = row.commitSha.length > 7 ? row.commitSha.slice(0, 7) : row.commitSha;

  return (
    <div className={`pipeline-other-row ${warning !== undefined ? 'pipeline-other-row-warning' : ''}`}>
      <div className="pipeline-other-main">
        <span className="pipeline-repo">
          {row.repo}
          {warning !== undefined && <WarningIndicator warning={warning} />}
          <PrBadge openPrCount={row.openPrCount} openPrs={row.openPrs} />
        </span>
        <Badge variant={otherRepoBadgeVariant(row.status)}>
          {row.status.replace(/-/g, ' ')}
        </Badge>
      </div>
      <div className="pipeline-other-meta">
        <code className="pipeline-commit">{commitShort}</code>
        <span className="pipeline-triggered">{formatWhen(row.triggeredAt)}</span>
        <a
          className="pipeline-run-link"
          href={row.runUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Run #{row.runId}
        </a>
      </div>
    </div>
  );
}

function AccessibilityReportLink({
  report,
}: {
  report: Pa11yAccessibilityReport | null | undefined;
}): JSX.Element {
  if (report === null || report === undefined) {
    return <Badge variant="neutral">A11y: no report</Badge>;
  }

  const variant = report.errorCount === 0 ? 'success' : 'warning';
  const label =
    report.errorCount === 0
      ? `A11y: 0 errors (${report.urlCount} URLs)`
      : `A11y: ${report.errorCount} error${report.errorCount === 1 ? '' : 's'}`;

  if (report.reportUrl === null) {
    return <Badge variant={variant}>{label}</Badge>;
  }

  return (
    <a
      className="pipeline-a11y-link"
      href={report.reportUrl}
      target="_blank"
      rel="noopener noreferrer"
      title={`WCAG 2.1 AA pa11y report for ${report.reportKey}`}
    >
      <Badge variant={variant}>{label}</Badge>
    </a>
  );
}

function MobileFrontendPipelineRow({
  row,
  warning,
  expanded,
  onToggle,
  accessibilityReport,
}: {
  row: OtherRepoPipeline;
  warning?: PipelineWarning;
  expanded: boolean;
  onToggle: () => void;
  accessibilityReport?: Pa11yAccessibilityReport | null;
}): JSX.Element {
  const commitShort = row.commitSha.length > 7 ? row.commitSha.slice(0, 7) : row.commitSha;
  const wf = row.mobileFrontendWorkflows;

  function formatWebDevTime(deployWebAppDev: PipelineStage): string | null {
    if (deployWebAppDev.status === 'passed' && deployWebAppDev.completedAt !== undefined && deployWebAppDev.completedAt !== '') {
      return formatWhen(deployWebAppDev.completedAt);
    }
    if (deployWebAppDev.status === 'running' && deployWebAppDev.startedAt !== undefined && deployWebAppDev.startedAt !== '') {
      return `deploying since ${formatWhen(deployWebAppDev.startedAt)}`;
    }
    return null;
  }

  function MobileMiniWideIndicator({
    workflows,
  }: {
    workflows: NonNullable<OtherRepoPipeline['mobileFrontendWorkflows']>;
  }): JSX.Element {
    const keys: Array<{
      key: keyof typeof workflows;
      label: string;
    }> = [
      { key: 'buildIos', label: 'iOS' },
      { key: 'buildAndroid', label: 'Android' },
      { key: 'deployWebAppDev', label: 'Web/Dev' },
      { key: 'deployWebAppStage', label: 'Web/Stage' },
      { key: 'deployWebAppProd', label: 'Web/Prod' },
    ];

    return (
      <div className="mini-stage-row mini-stage-row--wide">
        {keys.map(({ key, label }) => {
          const stage = workflows[key];
          const color = statusToColor(stage.status);
          return (
            <div
              key={key}
              className={`mini-stage mini-stage-${color}`}
              title={`${label}: ${stage.status}`}
            >
              <span className="mini-stage-label">{label}</span>
            </div>
          );
        })}
      </div>
    );
  }

  if (wf === undefined) {
    return (
      <div className="pipeline-microservice">
        <div className="pipeline-microservice-header">
          <div className="pipeline-microservice-title">
            <span className="pipeline-repo">
              {row.repo}
              {warning !== undefined && <WarningIndicator warning={warning} />}
            </span>
          </div>
        </div>
      </div>
    );
  }

  const webDevTime = formatWebDevTime(wf.deployWebAppDev);

  return (
    <div
      className={`pipeline-microservice ${expanded ? 'pipeline-microservice-expanded' : ''} ${
        warning !== undefined ? 'pipeline-microservice-warning' : ''
      }`}
    >
      <div className="pipeline-microservice-header">
        <button
          type="button"
          className="pipeline-expand-btn"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-label={expanded ? `Collapse ${row.repo}` : `Expand ${row.repo}`}
        >
          <span className="pipeline-expand-icon" aria-hidden>
            {expanded ? '▼' : '▶'}
          </span>
        </button>
        <div className="pipeline-microservice-title">
          <span className="pipeline-repo">
            {row.repo}
            {warning !== undefined && <WarningIndicator warning={warning} />}
            <PrBadge openPrCount={row.openPrCount} openPrs={row.openPrs} />
          </span>
          <span className="pipeline-meta">
            <span className="pipeline-branch">{row.branch}</span>
            <code className="pipeline-commit">{commitShort}</code>
            <span className="pipeline-triggered">{formatWhen(row.triggeredAt)}</span>
          </span>
        </div>

        {!expanded && (
          <div className="pipeline-summary">
            <MobileMiniWideIndicator workflows={wf} />
            {webDevTime !== null && (
              <span className="pipeline-deploy-time">Web (dev): {webDevTime}</span>
            )}
            <AccessibilityReportLink report={accessibilityReport} />
          </div>
        )}

        <a
          className="pipeline-run-link"
          href={row.runUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Run #{row.runId}
        </a>
      </div>

      {expanded && (
        <div className="pipeline-stages">
          <div className="pipeline-mobile-expanded-block">
            <div className="pipeline-mobile-web-line" aria-label="Web workflows">
              <StagePill
                name={wf.deployWebAppDev.name}
                status={wf.deployWebAppDev.status}
                durationSecs={wf.deployWebAppDev.durationSeconds ?? null}
                completedAt={wf.deployWebAppDev.completedAt ?? null}
              />
              <StagePill
                name={wf.deployWebAppStage.name}
                status={wf.deployWebAppStage.status}
                durationSecs={wf.deployWebAppStage.durationSeconds ?? null}
                completedAt={wf.deployWebAppStage.completedAt ?? null}
              />
              <StagePill
                name={wf.deployWebAppProd.name}
                status={wf.deployWebAppProd.status}
                durationSecs={wf.deployWebAppProd.durationSeconds ?? null}
                completedAt={wf.deployWebAppProd.completedAt ?? null}
              />
              <AccessibilityReportLink report={accessibilityReport} />
            </div>
            <div className="pipeline-mobile-native-line" aria-label="Native builds">
              <StagePill
                name={wf.buildIos.name}
                status={wf.buildIos.status}
                durationSecs={wf.buildIos.durationSeconds ?? null}
                completedAt={wf.buildIos.completedAt ?? null}
              />
              <StagePill
                name={wf.buildAndroid.name}
                status={wf.buildAndroid.status}
                durationSecs={wf.buildAndroid.durationSeconds ?? null}
                completedAt={wf.buildAndroid.completedAt ?? null}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ObservabilityPipelineRow({
  row,
  warning,
  expanded,
  onToggle,
}: {
  row: OtherRepoPipeline;
  warning?: PipelineWarning;
  expanded: boolean;
  onToggle: () => void;
}): JSX.Element {
  const commitShort = row.commitSha.length > 7 ? row.commitSha.slice(0, 7) : row.commitSha;

  const workflows = row.observabilityWorkflows;
  const stageOrder: PipelineStage[] = [
    workflows?.build,
    workflows?.integrationTests,
    workflows?.deploy,
  ].filter((s): s is PipelineStage => s !== undefined);

  function formatDeployTimeForObservability(stages: {
    deploy: PipelineStage;
  }): string | null {
    const deployStage = stages.deploy;
    if (deployStage.status === 'passed' && deployStage.completedAt !== undefined && deployStage.completedAt !== '') {
      return formatWhen(deployStage.completedAt);
    }
    if (deployStage.status === 'running' && deployStage.startedAt !== undefined && deployStage.startedAt !== '') {
      return `deploying since ${formatWhen(deployStage.startedAt)}`;
    }
    return null;
  }

  function ObservabilityMiniStageIndicator({
    stages,
  }: {
    stages: {
      build: PipelineStage;
      integrationTests: PipelineStage;
      deploy: PipelineStage;
    };
  }): JSX.Element {
    const miniKeys: Array<{ key: 'build' | 'integrationTests' | 'deploy'; label: string }> = [
      { key: 'build', label: 'Build' },
      { key: 'integrationTests', label: 'Int' },
      { key: 'deploy', label: 'Dep' },
    ];

    return (
      <div className="mini-stage-row">
        {miniKeys.map(({ key, label }) => {
          const stage = stages[key];
          const color = statusToColor(stage.status);
          return (
            <div
              key={key}
              className={`mini-stage mini-stage-${color}`}
              title={`${label}: ${stage.status}`}
            >
              <span className="mini-stage-label">{label}</span>
            </div>
          );
        })}
      </div>
    );
  }

  // Observability repos should always include this payload; keep UI resilient.
  if (workflows === undefined) {
    return (
      <div className="pipeline-microservice">
        <div className="pipeline-microservice-header">
          <div className="pipeline-microservice-title">
            <span className="pipeline-repo">{row.repo}</span>
          </div>
        </div>
      </div>
    );
  }

  const deployTime = formatDeployTimeForObservability({ deploy: workflows.deploy });

  return (
    <div
      className={`pipeline-microservice ${expanded ? 'pipeline-microservice-expanded' : ''} ${
        warning !== undefined ? 'pipeline-microservice-warning' : ''
      }`}
    >
      <div className="pipeline-microservice-header">
        <button
          type="button"
          className="pipeline-expand-btn"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-label={expanded ? `Collapse ${row.repo}` : `Expand ${row.repo}`}
        >
          <span className="pipeline-expand-icon" aria-hidden>
            {expanded ? '▼' : '▶'}
          </span>
        </button>
        <div className="pipeline-microservice-title">
          <span className="pipeline-repo">
            {row.repo}
            {warning !== undefined && <WarningIndicator warning={warning} />}
            <PrBadge openPrCount={row.openPrCount} openPrs={row.openPrs} />
          </span>
          <span className="pipeline-meta">
            <span className="pipeline-branch">{row.branch}</span>
            <code className="pipeline-commit">{commitShort}</code>
            <span className="pipeline-triggered">{formatWhen(row.triggeredAt)}</span>
          </span>
        </div>

        {!expanded && (
          <div className="pipeline-summary">
            <ObservabilityMiniStageIndicator stages={workflows} />
            {deployTime !== null && (
              <span className="pipeline-deploy-time">Deployed: {deployTime}</span>
            )}
          </div>
        )}

        <a
          className="pipeline-run-link"
          href={row.runUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Run #{row.runId}
        </a>
      </div>

      {expanded && (
        <div className="pipeline-stages">
          {stageOrder.map((stage) => (
            <StagePill
              key={stage.name}
              name={stage.name}
              status={stage.status}
              durationSecs={stage.durationSeconds ?? null}
              completedAt={stage.completedAt ?? null}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CloudFormationPipelineRow({
  row,
  warning,
  expanded,
  onToggle,
}: {
  row: OtherRepoPipeline;
  warning?: PipelineWarning;
  expanded: boolean;
  onToggle: () => void;
}): JSX.Element {
  const commitShort = row.commitSha.length > 7 ? row.commitSha.slice(0, 7) : row.commitSha;
  const wf = row.cloudFormationWorkflows;

  const CF_WORKFLOW_KEYS: Array<{
    key: keyof NonNullable<OtherRepoPipeline['cloudFormationWorkflows']>;
    shortLabel: string;
    fullLabel: string;
  }> = [
    { key: 'cart', shortLabel: 'cart', fullLabel: 'Cart' },
    { key: 'catalog', shortLabel: 'cat', fullLabel: 'Catalog' },
    { key: 'consent', shortLabel: 'cons', fullLabel: 'Consent' },
    { key: 'delivery', shortLabel: 'del', fullLabel: 'Delivery' },
    { key: 'image', shortLabel: 'img', fullLabel: 'Image' },
    { key: 'notification', shortLabel: 'notif', fullLabel: 'Notification' },
    { key: 'observabilityApi', shortLabel: 'obs-api', fullLabel: 'Obs API' },
    { key: 'order', shortLabel: 'order', fullLabel: 'Order' },
    { key: 'role', shortLabel: 'role', fullLabel: 'Role' },
    { key: 'tax', shortLabel: 'tax', fullLabel: 'Tax' },
    { key: 'user', shortLabel: 'user', fullLabel: 'User' },
    { key: 'envController', shortLabel: 'env-ctl', fullLabel: 'Env Controller' },
  ];

  const STATUS_LABELS: Record<StageStatus, string> = {
    passed: 'Passed',
    failed: 'Failed',
    running: 'Running',
    blocked: 'Blocked',
    'pending-gate': 'Pending',
    'not-implemented': 'N/A',
  };

  function formatDuration(secs: number | undefined): string | null {
    if (secs === undefined) return null;
    const mins = Math.floor(secs / 60);
    const remainingSecs = secs % 60;
    return mins > 0 ? `${mins}m ${remainingSecs}s` : `${remainingSecs}s`;
  }

  function formatCompletedAt(iso: string | undefined): string | null {
    if (iso === undefined || iso === '') return null;
    return formatDateTime(iso, { dateStyle: 'short', timeStyle: 'short' });
  }

  function CloudFormationMiniIndicator({
    workflows,
  }: {
    workflows: NonNullable<OtherRepoPipeline['cloudFormationWorkflows']>;
  }): JSX.Element {
    return (
      <div className="mini-stage-row mini-stage-row--wide">
        {CF_WORKFLOW_KEYS.map(({ key, shortLabel, fullLabel }) => {
          const stage = workflows[key];
          const color = statusToColor(stage.status);
          return (
            <div
              key={key}
              className={`mini-stage mini-stage-${color}`}
              title={`${fullLabel}: ${stage.status}`}
            >
              <span className="mini-stage-label">{shortLabel}</span>
            </div>
          );
        })}
      </div>
    );
  }

  function CloudFormationStageCard({
    label,
    stage,
  }: {
    label: string;
    stage: PipelineStage;
  }): JSX.Element {
    const duration = formatDuration(stage.durationSeconds);
    const completed = formatCompletedAt(stage.completedAt);
    const hasUrl = stage.url !== undefined && stage.url !== '';

    const content = (
      <>
        <div className="cf-stage-line1">
          <span className="cf-stage-name">{label}</span>
          <span className="cf-stage-status">{STATUS_LABELS[stage.status]}</span>
        </div>
        {completed !== null && (
          <div className="cf-stage-line2">
            <span className="cf-stage-completed">{completed}</span>
          </div>
        )}
        {duration !== null && (
          <div className="cf-stage-line3">
            <span className="cf-stage-duration">{duration}</span>
          </div>
        )}
      </>
    );

    if (hasUrl) {
      return (
        <a
          href={stage.url}
          target="_blank"
          rel="noopener noreferrer"
          className={`cf-stage-card cf-stage-card--link stage-${stage.status}`}
          title={`View ${label} workflow on GitHub`}
        >
          {content}
        </a>
      );
    }

    return (
      <div className={`cf-stage-card stage-${stage.status}`}>
        {content}
      </div>
    );
  }

  if (wf === undefined) {
    return (
      <div className="pipeline-microservice">
        <div className="pipeline-microservice-header">
          <div className="pipeline-microservice-title">
            <span className="pipeline-repo">
              {row.repo}
              {warning !== undefined && <WarningIndicator warning={warning} />}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`pipeline-microservice ${expanded ? 'pipeline-microservice-expanded' : ''} ${
        warning !== undefined ? 'pipeline-microservice-warning' : ''
      }`}
    >
      <div className="pipeline-microservice-header">
        <button
          type="button"
          className="pipeline-expand-btn"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-label={expanded ? `Collapse ${row.repo}` : `Expand ${row.repo}`}
        >
          <span className="pipeline-expand-icon" aria-hidden>
            {expanded ? '▼' : '▶'}
          </span>
        </button>
        <div className="pipeline-microservice-title">
          <span className="pipeline-repo">
            {row.repo}
            {warning !== undefined && <WarningIndicator warning={warning} />}
            <PrBadge openPrCount={row.openPrCount} openPrs={row.openPrs} />
          </span>
          <span className="pipeline-meta">
            <span className="pipeline-branch">{row.branch}</span>
            <code className="pipeline-commit">{commitShort}</code>
            <span className="pipeline-triggered">{formatWhen(row.triggeredAt)}</span>
          </span>
        </div>

        {!expanded && (
          <div className="pipeline-summary">
            <CloudFormationMiniIndicator workflows={wf} />
          </div>
        )}

        <a
          className="pipeline-run-link"
          href={row.runUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Run #{row.runId}
        </a>
      </div>

      {expanded && (
        <div className="pipeline-stages pipeline-stages--cf-grid">
          {CF_WORKFLOW_KEYS.map(({ key, fullLabel }) => {
            const stage = wf[key];
            return <CloudFormationStageCard key={key} label={fullLabel} stage={stage} />;
          })}
        </div>
      )}
    </div>
  );
}

function StaticAssetsPipelineRow({
  row,
  warning,
  expanded,
  onToggle,
}: {
  row: OtherRepoPipeline;
  warning?: PipelineWarning;
  expanded: boolean;
  onToggle: () => void;
}): JSX.Element {
  const commitShort = row.commitSha.length > 7 ? row.commitSha.slice(0, 7) : row.commitSha;
  const wf = row.staticAssetsWorkflows;

  const SA_WORKFLOW_KEYS: Array<{
    key: keyof NonNullable<OtherRepoPipeline['staticAssetsWorkflows']>;
    shortLabel: string;
    fullLabel: string;
  }> = [
    { key: 'dev', shortLabel: 'dev', fullLabel: 'Deploy Dev' },
    { key: 'stage', shortLabel: 'stage', fullLabel: 'Deploy Stage' },
    { key: 'prod', shortLabel: 'prod', fullLabel: 'Deploy Prod' },
    { key: 'copy', shortLabel: 'copy', fullLabel: 'Copy Between Environments' },
  ];

  const STATUS_LABELS: Record<StageStatus, string> = {
    passed: 'Passed',
    failed: 'Failed',
    running: 'Running',
    blocked: 'Blocked',
    'pending-gate': 'Pending',
    'not-implemented': 'N/A',
  };

  function formatDuration(secs: number | undefined): string | null {
    if (secs === undefined) return null;
    const mins = Math.floor(secs / 60);
    const remainingSecs = secs % 60;
    return mins > 0 ? `${mins}m ${remainingSecs}s` : `${remainingSecs}s`;
  }

  function formatCompletedAt(iso: string | undefined): string | null {
    if (iso === undefined || iso === '') return null;
    return formatDateTime(iso, { dateStyle: 'short', timeStyle: 'short' });
  }

  function StaticAssetsMiniIndicator({
    workflows,
  }: {
    workflows: NonNullable<OtherRepoPipeline['staticAssetsWorkflows']>;
  }): JSX.Element {
    return (
      <div className="mini-stage-row">
        {SA_WORKFLOW_KEYS.map(({ key, shortLabel, fullLabel }) => {
          const stage = workflows[key];
          const color = statusToColor(stage.status);
          return (
            <div
              key={key}
              className={`mini-stage mini-stage-${color}`}
              title={`${fullLabel}: ${stage.status}`}
            >
              <span className="mini-stage-label">{shortLabel}</span>
            </div>
          );
        })}
      </div>
    );
  }

  function StaticAssetsStageCard({
    label,
    stage,
  }: {
    label: string;
    stage: PipelineStage;
  }): JSX.Element {
    const duration = formatDuration(stage.durationSeconds);
    const completed = formatCompletedAt(stage.completedAt);
    const hasUrl = stage.url !== undefined && stage.url !== '';

    const content = (
      <>
        <div className="cf-stage-line1">
          <span className="cf-stage-name">{label}</span>
          <span className="cf-stage-status">{STATUS_LABELS[stage.status]}</span>
        </div>
        {completed !== null && (
          <div className="cf-stage-line2">
            <span className="cf-stage-completed">{completed}</span>
          </div>
        )}
        {duration !== null && (
          <div className="cf-stage-line3">
            <span className="cf-stage-duration">{duration}</span>
          </div>
        )}
      </>
    );

    if (hasUrl) {
      return (
        <a
          href={stage.url}
          target="_blank"
          rel="noopener noreferrer"
          className={`cf-stage-card cf-stage-card--link stage-${stage.status}`}
          title={`View ${label} workflow on GitHub`}
        >
          {content}
        </a>
      );
    }

    return (
      <div className={`cf-stage-card stage-${stage.status}`}>
        {content}
      </div>
    );
  }

  if (wf === undefined) {
    return (
      <div className="pipeline-microservice">
        <div className="pipeline-microservice-header">
          <div className="pipeline-microservice-title">
            <span className="pipeline-repo">
              {row.repo}
              {warning !== undefined && <WarningIndicator warning={warning} />}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`pipeline-microservice ${expanded ? 'pipeline-microservice-expanded' : ''} ${
        warning !== undefined ? 'pipeline-microservice-warning' : ''
      }`}
    >
      <div className="pipeline-microservice-header">
        <button
          type="button"
          className="pipeline-expand-btn"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-label={expanded ? `Collapse ${row.repo}` : `Expand ${row.repo}`}
        >
          <span className="pipeline-expand-icon" aria-hidden>
            {expanded ? '▼' : '▶'}
          </span>
        </button>
        <div className="pipeline-microservice-title">
          <span className="pipeline-repo">
            {row.repo}
            {warning !== undefined && <WarningIndicator warning={warning} />}
            <PrBadge openPrCount={row.openPrCount} openPrs={row.openPrs} />
          </span>
          <span className="pipeline-meta">
            <span className="pipeline-branch">{row.branch}</span>
            <code className="pipeline-commit">{commitShort}</code>
            <span className="pipeline-triggered">{formatWhen(row.triggeredAt)}</span>
          </span>
        </div>

        {!expanded && (
          <div className="pipeline-summary">
            <StaticAssetsMiniIndicator workflows={wf} />
          </div>
        )}

        <a
          className="pipeline-run-link"
          href={row.runUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Run #{row.runId}
        </a>
      </div>

      {expanded && (
        <div className="pipeline-stages">
          {SA_WORKFLOW_KEYS.map(({ key, fullLabel }) => {
            const stage = wf[key];
            return <StaticAssetsStageCard key={key} label={fullLabel} stage={stage} />;
          })}
        </div>
      )}
    </div>
  );
}

function DatabaseSchemaPipelineRow({
  row,
  warning,
  expanded,
  onToggle,
}: {
  row: OtherRepoPipeline;
  warning?: PipelineWarning;
  expanded: boolean;
  onToggle: () => void;
}): JSX.Element {
  const commitShort = row.commitSha.length > 7 ? row.commitSha.slice(0, 7) : row.commitSha;
  const wf = row.databaseSchemaWorkflows;

  const DS_WORKFLOW_KEYS: Array<{
    key: keyof NonNullable<OtherRepoPipeline['databaseSchemaWorkflows']>;
    shortLabel: string;
    fullLabel: string;
  }> = [
    { key: 'copyDb', shortLabel: 'Copy DB', fullLabel: 'Copy Database' },
    { key: 'deployAll', shortLabel: 'Deploy All', fullLabel: 'Deploy Schema to All' },
    { key: 'usWest1', shortLabel: 'us-west-1', fullLabel: 'Deploy Schema to us-west-1' },
    { key: 'usEast2', shortLabel: 'us-east-2', fullLabel: 'Deploy Schema to us-east-2' },
  ];

  const STATUS_LABELS: Record<StageStatus, string> = {
    passed: 'Passed',
    failed: 'Failed',
    running: 'Running',
    blocked: 'Blocked',
    'pending-gate': 'Pending',
    'not-implemented': 'N/A',
  };

  function formatDuration(secs: number | undefined): string | null {
    if (secs === undefined) return null;
    const mins = Math.floor(secs / 60);
    const remainingSecs = secs % 60;
    return mins > 0 ? `${mins}m ${remainingSecs}s` : `${remainingSecs}s`;
  }

  function formatCompletedAt(iso: string | undefined): string | null {
    if (iso === undefined || iso === '') return null;
    return formatDateTime(iso, { dateStyle: 'short', timeStyle: 'short' });
  }

  function DatabaseSchemaMiniIndicator({
    workflows,
  }: {
    workflows: NonNullable<OtherRepoPipeline['databaseSchemaWorkflows']>;
  }): JSX.Element {
    return (
      <div className="mini-stage-row">
        {DS_WORKFLOW_KEYS.map(({ key, shortLabel, fullLabel }) => {
          const stage = workflows[key];
          const color = statusToColor(stage.status);
          return (
            <div
              key={key}
              className={`mini-stage mini-stage-${color}`}
              title={`${fullLabel}: ${stage.status}`}
            >
              <span className="mini-stage-label">{shortLabel}</span>
            </div>
          );
        })}
      </div>
    );
  }

  function DatabaseSchemaStageCard({
    label,
    stage,
  }: {
    label: string;
    stage: PipelineStage;
  }): JSX.Element {
    const duration = formatDuration(stage.durationSeconds);
    const completed = formatCompletedAt(stage.completedAt);
    const hasUrl = stage.url !== undefined && stage.url !== '';

    const content = (
      <>
        <div className="cf-stage-line1">
          <span className="cf-stage-name">{label}</span>
          <span className="cf-stage-status">{STATUS_LABELS[stage.status]}</span>
        </div>
        {completed !== null && (
          <div className="cf-stage-line2">
            <span className="cf-stage-completed">{completed}</span>
          </div>
        )}
        {duration !== null && (
          <div className="cf-stage-line3">
            <span className="cf-stage-duration">{duration}</span>
          </div>
        )}
      </>
    );

    if (hasUrl) {
      return (
        <a
          href={stage.url}
          target="_blank"
          rel="noopener noreferrer"
          className={`cf-stage-card cf-stage-card--link stage-${stage.status}`}
          title={`View ${label} workflow on GitHub`}
        >
          {content}
        </a>
      );
    }

    return (
      <div className={`cf-stage-card stage-${stage.status}`}>
        {content}
      </div>
    );
  }

  if (wf === undefined) {
    return (
      <div className="pipeline-microservice">
        <div className="pipeline-microservice-header">
          <div className="pipeline-microservice-title">
            <span className="pipeline-repo">
              {row.repo}
              {warning !== undefined && <WarningIndicator warning={warning} />}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`pipeline-microservice ${expanded ? 'pipeline-microservice-expanded' : ''} ${
        warning !== undefined ? 'pipeline-microservice-warning' : ''
      }`}
    >
      <div className="pipeline-microservice-header">
        <button
          type="button"
          className="pipeline-expand-btn"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-label={expanded ? `Collapse ${row.repo}` : `Expand ${row.repo}`}
        >
          <span className="pipeline-expand-icon" aria-hidden>
            {expanded ? '▼' : '▶'}
          </span>
        </button>
        <div className="pipeline-microservice-title">
          <span className="pipeline-repo">
            {row.repo}
            {warning !== undefined && <WarningIndicator warning={warning} />}
            <PrBadge openPrCount={row.openPrCount} openPrs={row.openPrs} />
          </span>
          <span className="pipeline-meta">
            <span className="pipeline-branch">{row.branch}</span>
            <code className="pipeline-commit">{commitShort}</code>
            <span className="pipeline-triggered">{formatWhen(row.triggeredAt)}</span>
          </span>
        </div>

        {!expanded && (
          <div className="pipeline-summary">
            <DatabaseSchemaMiniIndicator workflows={wf} />
          </div>
        )}

        <a
          className="pipeline-run-link"
          href={row.runUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Run #{row.runId}
        </a>
      </div>

      {expanded && (
        <div className="pipeline-stages">
          {DS_WORKFLOW_KEYS.map(({ key, fullLabel }) => {
            const stage = wf[key];
            return <DatabaseSchemaStageCard key={key} label={fullLabel} stage={stage} />;
          })}
        </div>
      )}
    </div>
  );
}

type DeployEnv = 'dev' | 'staging' | 'prod';

const DEPLOY_STAGE_MAP: Record<DeployEnv, keyof MicroservicePipeline['stages']> = {
  dev: 'ecsDeployDev',
  staging: 'ecsDeployStaging',
  prod: 'ecsDeployProd',
};

/**
 * Build repo → ISO timestamp for ECS last PRIMARY deployment in prod (DescribeServices).
 * Used so "Last Deployed / Prod" shows real rollout times when main is ahead of the image
 * correlated for `ecsDeployProd` (often `pending-gate` though prod still has prior deploys).
 */
function prodEcsLastDeployedMapFromServices(services: EcsService[]): Map<string, string> {
  const m = new Map<string, string>();
  for (const svc of services) {
    const prod = svc.environments.find((e) => e.environment === 'prod');
    const at = prod?.lastDeployedAt;
    if (at !== undefined && at !== '') {
      m.set(svc.serviceName, at);
    }
  }
  return m;
}

function getDeploySummaryTime(
  repo: string,
  stages: MicroservicePipeline['stages'],
  env: DeployEnv,
  prodEcsLastDeployedByRepo?: Map<string, string>,
): string | null {
  const stage = stages[DEPLOY_STAGE_MAP[env]];

  if (env === 'prod') {
    const ecsIso = prodEcsLastDeployedByRepo?.get(repo);
    const hasEcs = ecsIso !== undefined && ecsIso !== '';

    if (stage.status === 'running' && stage.startedAt !== undefined && stage.startedAt !== '') {
      return 'deploying...';
    }
    if (stage.status === 'failed') {
      if (stage.completedAt !== undefined && stage.completedAt !== '') {
        return formatWhen(stage.completedAt);
      }
      return 'Failed';
    }
    if (stage.status === 'passed' && stage.completedAt !== undefined && stage.completedAt !== '') {
      return formatWhen(stage.completedAt);
    }
    if (hasEcs) {
      return formatWhen(ecsIso);
    }
    if (stage.status === 'pending-gate') {
      return 'Awaiting promote';
    }
    if (stage.status === 'not-implemented' || stage.status === 'blocked') {
      return '—';
    }
    return null;
  }

  if (stage.status === 'passed' && stage.completedAt !== undefined && stage.completedAt !== '') {
    return formatWhen(stage.completedAt);
  }
  if (stage.status === 'running' && stage.startedAt !== undefined && stage.startedAt !== '') {
    return `deploying...`;
  }
  if (stage.status === 'pending-gate') {
    return 'Pending';
  }
  if (stage.status === 'failed') {
    if (stage.completedAt !== undefined && stage.completedAt !== '') {
      return formatWhen(stage.completedAt);
    }
    return 'Failed';
  }
  if (stage.status === 'not-implemented' || stage.status === 'blocked') {
    return '—';
  }
  return null;
}

function shortenServiceName(repo: string): string {
  // Special cases for observability repos
  if (repo === 'observability-api') return 'obs-api';
  if (repo === 'observability-dashboard') return 'obs-dashboard';
  // Remove common suffixes to shorten display names
  return repo
    .replace(/-management-service$/, '')
    .replace(/-service$/, '')
    .replace(/^blooph-/, '')
    .replace(/-frontend$/, '');
}

function DeploySummaryCard({
  microservices,
  otherRepos,
  selectedEnv,
  onEnvChange,
  prodEcsLastDeployedByRepo,
}: {
  microservices: MicroservicePipeline[];
  otherRepos: OtherRepoPipeline[];
  selectedEnv: DeployEnv;
  onEnvChange: (env: DeployEnv) => void;
  /** ECS prod rollout times by `serviceName` (matches pipeline `repo`). Prod tab only. */
  prodEcsLastDeployedByRepo?: Map<string, string>;
}): JSX.Element {
  const mobileFrontend = otherRepos.find((r) => r.repo === 'blooph-mobile-frontend');

  return (
    <div className="deploy-summary-card">
      <div className="deploy-summary-header">
        <h3 className="deploy-summary-title">Last Deployed</h3>
        <div className="deploy-env-selector">
          {(['dev', 'staging', 'prod'] as DeployEnv[]).map((env) => (
            <button
              key={env}
              type="button"
              className={`deploy-env-btn ${selectedEnv === env ? 'deploy-env-btn-active' : ''}`}
              onClick={(): void => onEnvChange(env)}
            >
              {env.charAt(0).toUpperCase() + env.slice(1)}
            </button>
          ))}
        </div>
      </div>
      <ul className="deploy-summary-list">
        {microservices.map((svc) => {
          const deployTime = getDeploySummaryTime(
            svc.repo,
            svc.stages,
            selectedEnv,
            selectedEnv === 'prod' ? prodEcsLastDeployedByRepo : undefined,
          );
          const displayName = shortenServiceName(svc.repo);
          return (
            <li key={svc.repo} className="deploy-summary-row">
              <span className="deploy-summary-name" title={svc.repo}>{displayName}</span>
              <span className={`deploy-summary-time ${deployTime === '—' ? 'deploy-summary-time-na' : ''}`}>
                {deployTime ?? '—'}
              </span>
            </li>
          );
        })}
        {mobileFrontend !== undefined && (
          <li className="deploy-summary-row deploy-summary-row-other">
            <span className="deploy-summary-name" title={mobileFrontend.repo}>frontend</span>
            <span className="deploy-summary-time">
              {mobileFrontend.completedAt !== undefined && mobileFrontend.completedAt !== ''
                ? formatWhen(mobileFrontend.completedAt)
                : '—'}
            </span>
          </li>
        )}
      </ul>
    </div>
  );
}

function PipelineLoadingIndicator({ elapsedSeconds }: { elapsedSeconds: number }): JSX.Element {
  const progress = Math.min(90, 15 + elapsedSeconds * 12);
  const phases = [
    'Connecting to observability API...',
    'Fetching repository workflow runs...',
    'Collecting job status and timestamps...',
    'Rendering pipeline cards...',
  ];
  const phaseIndex = Math.min(phases.length - 1, Math.floor(elapsedSeconds / 2));
  const phase = phases[phaseIndex] ?? phases[0];

  return (
    <div className="pipeline-loading" role="status" aria-live="polite" aria-label="Loading pipeline data">
      <p className="pipeline-loading-title">Loading pipeline data...</p>
      <p className="pipeline-loading-phase">{phase}</p>
      <div className="pipeline-loading-bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress)}>
        <div className="pipeline-loading-bar-fill" style={{ width: `${progress}%` }} />
      </div>
    </div>
  );
}

export function PipelineTab(): JSX.Element {
  const { mode, manualRefreshNonce, tabVisible } = useRefresh();
  const [data, setData] = useState<PipelineResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingElapsedSeconds, setLoadingElapsedSeconds] = useState(0);
  const [expandedRepos, setExpandedRepos] = useState<Set<string>>(() => new Set());
  const [deployEnv, setDeployEnv] = useState<DeployEnv>('dev');
  const [prodEcsLastDeployedByRepo, setProdEcsLastDeployedByRepo] = useState<Map<string, string>>(
    () => new Map(),
  );
  const [accessibilityBySha, setAccessibilityBySha] = useState<
    Map<string, Pa11yAccessibilityReport>
  >(() => new Map());

  useEffect(() => {
    if (!tabVisible) {
      return undefined;
    }

    let cancelled = false;

    function runFetch(silent: boolean): void {
      if (!silent) {
        setIsLoading(true);
        setLoadingElapsedSeconds(0);
        setError(null);
      }

      void getPipeline()
        .then(async (pipelineResponse) => {
          if (!cancelled) {
            setData(pipelineResponse);
          }
          try {
            const accessibilityResponse = await getAccessibility(50);
            if (!cancelled) {
              const bySha = new Map<string, Pa11yAccessibilityReport>();
              for (const report of accessibilityResponse.reports) {
                bySha.set(report.shortSha.toLowerCase(), report);
              }
              setAccessibilityBySha(bySha);
            }
          } catch {
            if (!cancelled) {
              setAccessibilityBySha(new Map());
            }
          }
        })
        .catch((err: unknown) => {
          if (cancelled || silent) {
            return;
          }
          if (err instanceof ApiError) {
            setError(`Failed to load pipeline data (${err.status}).`);
          } else {
            setError('Failed to load pipeline data.');
          }
        })
        .finally(() => {
          if (!cancelled && !silent) {
            setIsLoading(false);
          }
        });
    }

    runFetch(false);

    let intervalId: number | undefined;
    if (mode === 'auto') {
      intervalId = window.setInterval(() => runFetch(true), POLL_INTERVAL_PIPELINE_MS);
    }

    return () => {
      cancelled = true;
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
      }
    };
  }, [mode, tabVisible, manualRefreshNonce]);

  useEffect(() => {
    if (!tabVisible || data === null) {
      return undefined;
    }
    let cancelled = false;
    void getServices('prod')
      .then((res) => {
        if (!cancelled) {
          setProdEcsLastDeployedByRepo(prodEcsLastDeployedMapFromServices(res.services));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setProdEcsLastDeployedByRepo(new Map());
        }
      });
    return () => {
      cancelled = true;
    };
  }, [tabVisible, data?.fetchedAt, manualRefreshNonce]);

  useEffect(() => {
    if (!isLoading) return;
    const timer = window.setInterval(() => {
      setLoadingElapsedSeconds((prev) => prev + 1);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [isLoading]);

  function toggleRepo(repo: string): void {
    setExpandedRepos((prev) => {
      const next = new Set(prev);
      if (next.has(repo)) {
        next.delete(repo);
      } else {
        next.add(repo);
      }
      return next;
    });
  }

  // Build a map of warnings by repo for quick lookup
  const warningsByRepo = new Map<string, PipelineWarning>();
  const generalWarnings: PipelineWarning[] = [];

  if (data?.warnings !== undefined) {
    for (const w of data.warnings) {
      // Check if this warning is for a specific repo in our lists
      const isRepoSpecific =
        data.microservices.some((m) => m.repo === w.repo) ||
        data.otherRepos.some((o) => o.repo === w.repo);

      if (isRepoSpecific) {
        warningsByRepo.set(w.repo, w);
      } else {
        generalWarnings.push(w);
      }
    }
  }

  return (
    <div className="pipeline-tab">
      <div className="pipeline-header-row">
        <div className="pipeline-header-info">
          <h2>Pipeline Status</h2>
          <p className="tab-lead">
            End-to-end status per repository (main branch). The header environment selector applies to Services and
            Database only — it does not change pipeline data.
          </p>
          {!isLoading && error === null && data !== null && (
            <p className="fetched-at">Last updated {formatWhen(data.fetchedAt)}</p>
          )}
        </div>
        {!isLoading && error === null && data !== null && (
          <>
            <LastProdPromoteCard lastDeployedProdAt={data.lastDeployedProdAt} />
            <DeploySummaryCard
              microservices={data.microservices}
              otherRepos={data.otherRepos}
              selectedEnv={deployEnv}
              onEnvChange={setDeployEnv}
              prodEcsLastDeployedByRepo={deployEnv === 'prod' ? prodEcsLastDeployedByRepo : undefined}
            />
          </>
        )}
      </div>

      {/* General warnings banner */}
      {generalWarnings.length > 0 && (
        <div className="pipeline-warnings-banner" role="alert">
          <span className="pipeline-warnings-icon">⚠️</span>
          <div className="pipeline-warnings-content">
            {generalWarnings.map((w) => (
              <p key={w.repo} className="pipeline-warning-message">
                <strong>{w.repo}:</strong> {w.message}
              </p>
            ))}
          </div>
        </div>
      )}

      {isLoading && <PipelineLoadingIndicator elapsedSeconds={loadingElapsedSeconds} />}
      {!isLoading && error !== null && <p className="placeholder">{error}</p>}
      {!isLoading && error === null && data !== null && (
        <>
          {(() => {
            const mobileRow = data.otherRepos.find((r) => r.repo === 'blooph-mobile-frontend');
            if (mobileRow === undefined) return null;
            return (
              <section className="pipeline-section" aria-label="Frontend pipeline">
                <h3 className="pipeline-section-title">Frontend</h3>
                <div className="pipeline-other-list">
                  <MobileFrontendPipelineRow
                    row={mobileRow}
                    warning={warningsByRepo.get(mobileRow.repo)}
                    expanded={expandedRepos.has(mobileRow.repo)}
                    onToggle={(): void => toggleRepo(mobileRow.repo)}
                    accessibilityReport={accessibilityBySha.get(
                      (mobileRow.commitSha.length > 7
                        ? mobileRow.commitSha.slice(0, 7)
                        : mobileRow.commitSha
                      ).toLowerCase(),
                    )}
                  />
                </div>
              </section>
            );
          })()}

          <section className="pipeline-section" aria-label="Microservice pipelines">
            <h3 className="pipeline-section-title">Microservices</h3>
            <div className="pipeline-list">
              {data.microservices.map((row) => (
                <MicroserviceRow
                  key={row.repo}
                  row={row}
                  expanded={expandedRepos.has(row.repo)}
                  onToggle={(): void => toggleRepo(row.repo)}
                  warning={warningsByRepo.get(row.repo)}
                  prodEcsLastDeployedByRepo={prodEcsLastDeployedByRepo}
                />
              ))}
            </div>
          </section>

          {(() => {
            const observabilityRepos = data.otherRepos.filter(
              (r) => r.repo === 'observability-api' || r.repo === 'observability-dashboard',
            );
            if (observabilityRepos.length === 0) return null;
            return (
              <section className="pipeline-section" aria-label="Observability pipelines">
                <h3 className="pipeline-section-title">Observability Pipelines</h3>
                <div className="pipeline-other-list">
                  {observabilityRepos.map((row) => (
                    <ObservabilityPipelineRow
                      key={row.repo}
                      row={row}
                      warning={warningsByRepo.get(row.repo)}
                      expanded={expandedRepos.has(row.repo)}
                      onToggle={(): void => toggleRepo(row.repo)}
                    />
                  ))}
                </div>
              </section>
            );
          })()}

          {(() => {
            const cloudFormationRow = data.otherRepos.find((r) => r.repo === 'cloud-formation');
            if (cloudFormationRow === undefined) return null;
            return (
              <section className="pipeline-section" aria-label="CloudFormation pipelines">
                <h3 className="pipeline-section-title">CloudFormation</h3>
                <div className="pipeline-other-list">
                  <CloudFormationPipelineRow
                    row={cloudFormationRow}
                    warning={warningsByRepo.get(cloudFormationRow.repo)}
                    expanded={expandedRepos.has(cloudFormationRow.repo)}
                    onToggle={(): void => toggleRepo(cloudFormationRow.repo)}
                  />
                </div>
              </section>
            );
          })()}

          {(() => {
            const staticAssetsRow = data.otherRepos.find((r) => r.repo === 'static-assets');
            if (staticAssetsRow === undefined) return null;
            return (
              <section className="pipeline-section" aria-label="Static Assets pipelines">
                <h3 className="pipeline-section-title">Static Assets</h3>
                <div className="pipeline-other-list">
                  <StaticAssetsPipelineRow
                    row={staticAssetsRow}
                    warning={warningsByRepo.get(staticAssetsRow.repo)}
                    expanded={expandedRepos.has(staticAssetsRow.repo)}
                    onToggle={(): void => toggleRepo(staticAssetsRow.repo)}
                  />
                </div>
              </section>
            );
          })()}

          {(() => {
            const databaseSchemaRow = data.otherRepos.find((r) => r.repo === 'database-schema');
            if (databaseSchemaRow === undefined) return null;
            return (
              <section className="pipeline-section" aria-label="Database Schema pipelines">
                <h3 className="pipeline-section-title">Database Schema</h3>
                <div className="pipeline-other-list">
                  <DatabaseSchemaPipelineRow
                    row={databaseSchemaRow}
                    warning={warningsByRepo.get(databaseSchemaRow.repo)}
                    expanded={expandedRepos.has(databaseSchemaRow.repo)}
                    onToggle={(): void => toggleRepo(databaseSchemaRow.repo)}
                  />
                </div>
              </section>
            );
          })()}

          <section className="pipeline-section" aria-label="Other repositories">
            <h3 className="pipeline-section-title">Other repositories</h3>
            <div className="pipeline-other-list">
              {data.otherRepos
                .filter(
                  (r) =>
                    r.repo !== 'observability-api' &&
                    r.repo !== 'observability-dashboard' &&
                    r.repo !== 'blooph-mobile-frontend' &&
                    r.repo !== 'cloud-formation' &&
                    r.repo !== 'static-assets' &&
                    r.repo !== 'database-schema',
                )
                .map((row) => (
                <OtherRepoRow
                  key={row.repo}
                  row={row}
                  warning={warningsByRepo.get(row.repo)}
                />
                ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
